import { ReportSettings } from './../../../shared/models/report-settings/report-settings';
import * as _ from 'underscore';
import {
  Component,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild, OnChanges, SimpleChanges
} from '@angular/core';
import { PropertyPhoto, BulkAssignComponent, PhotoHelper } from '../../shared/models';
import { ModalComponent } from 'app/internal/shared/components/modal';
import { BulkAssignComponentModalComponent } from '../bulk-assign-component-modal';
import { FiscalYear } from 'app/internal/shared/models';
import { Dictionary } from '@ngrx/entity';
import { BulkPhotoReportSettings } from '../../shared/models/bulk-photo-report-settings';
import { DxPopupTypes } from 'devextreme-angular/ui/popup';

@Component({
  selector: 'sp-photo-gallery',
  templateUrl: './photo-gallery.component.html',
  styleUrls: ['./photo-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhotoGalleryComponent implements OnChanges {
  @ViewChild('bulkDeletePhotos', { static: true }) public bulkDeletePhotosModal: ModalComponent;
  @ViewChild('bulkAssignPhotos', { static: true }) public bulkAssignPhotosModal: BulkAssignComponentModalComponent;
  @ViewChild('bulkUpdateReportSettings', { static: true }) public bulkUpdateReportSettingsModal: ModalComponent;


  @Input() photos: PropertyPhoto[];
  @Input() propertyId: string;
  @Input() fiscalYear: FiscalYear;
  @Input() reportSettings: ReportSettings;

  @Output() viewPhoto: EventEmitter<PropertyPhoto> = new EventEmitter<PropertyPhoto>();
  @Output() bulkDownload: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() bulkAssignComponent: EventEmitter<BulkAssignComponent> = new EventEmitter<BulkAssignComponent>();
  @Output() bulkUpdatePhotoReportSettings: EventEmitter<BulkPhotoReportSettings> = new EventEmitter<BulkPhotoReportSettings>();
  @Output() bulkDelete: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() sortChanged: EventEmitter<any> = new EventEmitter<any>();

  photosGroupedByYear: Dictionary<PropertyPhoto[]>;
  photoYears = [];
  selectedPhotoIds = [];
  photoToView: PropertyPhoto;
  bulkIncludePhotosInReport = true;
  sortBy = 'uploadDate';
  sortDesc = true;
  sortByName = 'Date Uploaded - Newest to Oldest';

  constructor(private ref: ChangeDetectorRef) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['photos'] && this.photos) {
      this.groupAndSortPhotos();
    }

    if (changes['reportSettings'] && this.reportSettings) {
      const sortDetail = PhotoHelper.getSortFromReportSettings(this.reportSettings);
      this.sortBy = sortDetail.sortBy;
      this.sortDesc = sortDetail.sortDesc;

      this.groupAndSortPhotos();
    }
  }

  groupAndSortPhotos() {

    this.photosGroupedByYear = this.sortBy == 'takenDate'
      ? _.groupBy(this.photos, function (photo) { return photo.takenYear; })
      : _.groupBy(this.photos, function (photo) { return photo.uploadYear; });


    Object.values(this.photosGroupedByYear).forEach((groupedPhotos) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      groupedPhotos = PhotoHelper.sort(groupedPhotos, this.sortBy, this.sortDesc);
    });

    this.photoYears = Object.keys(this.photosGroupedByYear).sort((a, b) => {

      let retVal = 0;
      if (a == b) { retVal = 0; } else if (a == null || a == 'null' || a < b) { retVal = -1; } else if (b == null || b == 'null' || a > b) { retVal = 1; }
      // console.log('Compare: ' + a + ',  ' + b + ' = ' + retVal);
      return retVal;
    });

    if (this.sortDesc) {
      this.photoYears = this.photoYears.reverse();
    }

    this.sortByName = this.sortBy == 'takenDate' ? 'Date Taken' : 'Date Uploaded';
    this.sortByName += this.sortDesc ? ' - Newest to Oldest' : ' - Oldest to Newest';
  }

  changeSortOrder(sortBy: string, sortDesc: boolean) {
    this.sortBy = sortBy;
    this.sortDesc = sortDesc;
    this.groupAndSortPhotos();

    const sortDetail = {
      sortBy: sortBy,
      sortDesc: sortDesc
    };
    this.sortChanged.emit(sortDetail);

    this.toggleAllPhotoSelection(false, null);
  }

  isPhotoSelected(photo: PropertyPhoto) {
    const existingIndex = this.selectedPhotoIds.indexOf(photo.propertyPhotoId);
    return existingIndex >= 0;
  }

  togglePhotoSelection(photo: PropertyPhoto, event: any) {
    event.stopPropagation();

    const existingIndex = this.selectedPhotoIds.indexOf(photo.propertyPhotoId);

    // is in list, so remove
    if (existingIndex >= 0) {
      this.selectedPhotoIds.splice(existingIndex, 1);
    } else {
      this.selectedPhotoIds.push(photo.propertyPhotoId);
    }
  }

  onViewPhoto(photo: PropertyPhoto, event: any) {
    event.stopPropagation();
    this.photoToView = photo;
    this.viewPhoto.emit(photo);
  }

  onHoverOverlayClicked(photo: PropertyPhoto, event: any) {
    if (this.getSelectedPhotoCount() > 0) {
      this.togglePhotoSelection(photo, event);
    } else {
      this.onViewPhoto(photo, event);
    }
  }

  getSelectedPhotoCount() {
    if (!this.selectedPhotoIds) {
      return 0;
    }
    return this.selectedPhotoIds.length;
  }

  toggleAllPhotoSelection(select: boolean, year: number) {
    if (select === true && year == null) {
      this.selectedPhotoIds = this.photos.map((p) => p.propertyPhotoId);
    } else if (select === true && year != null) {
      this.selectedPhotoIds = this.photosGroupedByYear[year].map((p) => p.propertyPhotoId);
    } else if (select === false) {
      this.selectedPhotoIds = [];
    }
  }

  toggleBulkIncludePhotosInReport() {
    this.bulkIncludePhotosInReport = !this.bulkIncludePhotosInReport;
  }

  onBulkUpdateReportSettings() {
    this.bulkIncludePhotosInReport = true;
    this.bulkUpdateReportSettingsModal.show();
  }

  onBulkReportSettingsConfirm() {
    const bulkReportSettingsEvent = new BulkPhotoReportSettings(this.bulkIncludePhotosInReport, this.selectedPhotoIds);
    this.bulkUpdatePhotoReportSettings.emit(bulkReportSettingsEvent);
  }

  onBulkDownload() {
    this.bulkDownload.emit(this.selectedPhotoIds);
  }

  onBulkAssignComponent() {
    this.bulkAssignPhotosModal.show();
  }

  onBulkAssignComponentConfirm(propertyComponentId: string) {
    const bulkAssignEvent = new BulkAssignComponent([], this.selectedPhotoIds);
    if (propertyComponentId) {
      bulkAssignEvent.propertyComponentIds = [propertyComponentId];
    }
    this.bulkAssignComponent.emit(bulkAssignEvent);
  }

  onBulkDelete() {
    this.bulkDeletePhotosModal.show();
  }

  onBulkDeleteConfirm() {
    this.bulkDelete.emit(this.selectedPhotoIds);
    this.selectedPhotoIds = [];
  }

  // called from parent component to clear the selected style when the right hand pane is closed
  clearPhotoToView() {
    this.photoToView = null;
    this.ref.detectChanges();
  }

  setPhotoToView(photo: PropertyPhoto) {
    this.photoToView = photo;
    const element = document.getElementById(photo.propertyPhotoId);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.ref.detectChanges();
  }

  onPopupShowing(e: DxPopupTypes.ShowingEvent) {
    if (this.getSelectedPhotoCount() > 0) {
      e.cancel = true;
    }
  }
}
